import type { ChangeEvent, ReactNode } from 'react';
import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  createCalendarGrid,
  daysOfWeek,
  getMonthDropdownOptions,
  getYearDropdownOptions,
  isToday,
  isWeekendDay,
} from './helpers';
import type { CalendarDayObject, YearAndMonth } from './helpers';

interface CalendarProps {
  yearAndMonth: YearAndMonth;
  onYearAndMonthChange: (yearAndMonth: YearAndMonth) => void;
  renderDay?: (calendarDayObject: CalendarDayObject) => ReactNode;
}

export function Calendar({
  yearAndMonth,
  onYearAndMonthChange,
  renderDay = () => null,
}: CalendarProps) {
  const [year, month] = yearAndMonth;

  const calendarGridDayObjects = useMemo(() => createCalendarGrid(year, month), [year, month]);

  const handleMonthNavBackButtonClick = () => {
    let nextYear = year;
    let nextMonth = month - 1;
    if (nextMonth === 0) {
      nextMonth = 12;
      nextYear = year - 1;
    }
    onYearAndMonthChange([nextYear, nextMonth]);
  };

  const handleMonthNavForwardButtonClick = () => {
    let nextYear = year;
    let nextMonth = month + 1;
    if (nextMonth === 13) {
      nextMonth = 1;
      nextYear = year + 1;
    }
    onYearAndMonthChange([nextYear, nextMonth]);
  };

  const handleMonthSelect = (evt: ChangeEvent<HTMLSelectElement>) => {
    onYearAndMonthChange([year, Number(evt.target.value)]);
  };

  const handleYearSelect = (evt: ChangeEvent<HTMLSelectElement>) => {
    onYearAndMonthChange([Number(evt.target.value), month]);
  };

  const selectClassName =
    'h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-medium text-slate-700 outline-none transition-colors hover:border-slate-300 focus-visible:border-slate-400 focus-visible:ring-2 focus-visible:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:focus-visible:ring-slate-700';

  const navButtonClassName =
    'inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100';

  return (
    <div className="flex h-full flex-col">
      {/* Navigation header: prev / next + month & year dropdowns */}
      <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleMonthNavBackButtonClick}
            aria-label="Previous month"
            className={navButtonClassName}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={handleMonthNavForwardButtonClick}
            aria-label="Next month"
            className={navButtonClassName}
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <select
          value={month}
          onChange={handleMonthSelect}
          aria-label="Month"
          className={selectClassName}
        >
          {getMonthDropdownOptions().map(({ value, label }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={handleYearSelect}
          aria-label="Year"
          className={selectClassName}
        >
          {getYearDropdownOptions(year).map(({ value, label }) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Days-of-week header */}
      <div className="grid shrink-0 grid-cols-7 border-b border-slate-200 dark:border-slate-700">
        {daysOfWeek.map((day, index) => (
          <div
            key={day}
            className={cn(
              'px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400',
              [0, 6].includes(index) && 'text-slate-400 dark:text-slate-500',
            )}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid flex-1 auto-rows-fr grid-cols-7 overflow-hidden">
        {calendarGridDayObjects.map((day) => (
          <div
            key={day.dateString}
            className={cn(
              'flex min-h-0 flex-col overflow-hidden border-b border-r border-slate-100 p-1.5 dark:border-slate-700/60',
              '[&:nth-child(7n)]:border-r-0',
              !day.isCurrentMonth && 'bg-slate-50/70 dark:bg-slate-900/40',
              day.isCurrentMonth && isWeekendDay(day.dateString) && 'bg-slate-50/40 dark:bg-slate-900/20',
            )}
          >
            {renderDay(day)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarDayHeader({ calendarDayObject }: { calendarDayObject: CalendarDayObject }) {
  const today = isToday(calendarDayObject.dateString);
  return (
    <div className="mb-1 flex shrink-0 justify-end">
      <span
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
          today
            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
            : calendarDayObject.isCurrentMonth
              ? 'text-slate-700 dark:text-slate-200'
              : 'text-slate-400 dark:text-slate-600',
        )}
      >
        {calendarDayObject.dayOfMonth}
      </span>
    </div>
  );
}
